import type { EdgeChange, NodeChange } from "@xyflow/react";
import type { Selection } from "./store";

/** Applies React Flow `select` changes to a Selection, returning the same ref
 *  when there were none. Drop these entries and React Flow's internal state
 *  diverges from the controlled prop — the "click twice to select" bug. */
export function applyNodeSelectionChanges(
  changes: NodeChange[],
  current: Selection,
): Selection {
  let working: Set<string> | null = null;
  for (const c of changes) {
    if (c.type !== "select") continue;
    if (!working) working = new Set(current.nodeIds);
    if (c.selected) working.add(c.id);
    else working.delete(c.id);
  }
  if (!working) return current;
  const nextNodeIds = Array.from(working);
  if (sameIds(nextNodeIds, current.nodeIds)) return current;
  return { nodeIds: nextNodeIds, cableIds: current.cableIds };
}

/** Edge counterpart to {@link applyNodeSelectionChanges}. */
export function applyEdgeSelectionChanges(
  changes: EdgeChange[],
  current: Selection,
): Selection {
  let working: Set<string> | null = null;
  for (const c of changes) {
    if (c.type !== "select") continue;
    if (!working) working = new Set(current.cableIds);
    if (c.selected) working.add(c.id);
    else working.delete(c.id);
  }
  if (!working) return current;
  const nextCableIds = Array.from(working);
  if (sameIds(nextCableIds, current.cableIds)) return current;
  return { nodeIds: current.nodeIds, cableIds: nextCableIds };
}

function sameIds(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
